import { graphql, readInlineData } from 'react-relay';

import {
    trackEvent,
    eventNameConstants,
    stepInteractionConstants,
    type TrackingObject,
} from 'dibs-tracking';

const { EVENT_PRODUCT_INTERACTION } = eventNameConstants;
const { STEP_INTERACTION_DISPLAY, STEP_INTERACTION_LINK_CLICK } = stepInteractionConstants;

import { type pricingGuidanceTracking_item$key } from './__generated__/pricingGuidanceTracking_item.graphql.ts';

const itemFragment = graphql`
    fragment pricingGuidanceTracking_item on Item @inline {
        pricingGuidance(
            isTrade: $isTrade
            buyerId: $userId
            userCountryCode: $userCountryCode
            priceBookName: $priceBookName
        ) {
            price {
                amount
            }
            minAmount {
                amount
            }
            maxAmount {
                amount
            }
        }
    }
`;

export const getEstimationTrackingValue = ({
    item: itemRef,
}: {
    item: pricingGuidanceTracking_item$key | null;
}): string => {
    const item = readInlineData(itemFragment, itemRef);
    const { price, minAmount, maxAmount } = item?.pricingGuidance || {};
    const itemPrice = price?.amount || 0;
    const rangeMin = minAmount?.amount || 0;
    const rangeMax = maxAmount?.amount || 0;

    const estimateCopy = '1stdibs estimate';

    if (itemPrice < rangeMin) {
        return `below ${estimateCopy}`;
    }

    if (itemPrice > rangeMax) {
        return `above ${estimateCopy}`;
    }

    return `within ${estimateCopy}`;
};

const getBaseTrackingObject = ({
    item,
}: {
    item: pricingGuidanceTracking_item$key;
}): TrackingObject => {
    const step_type = `pdp - ${getEstimationTrackingValue({ item })}`;

    return {
        category: 'product',
        label: step_type,
        eventName: EVENT_PRODUCT_INTERACTION,
        step_type,
    };
};

export const trackPricingGuidanceRangeDisplay = ({
    item,
}: {
    item: pricingGuidanceTracking_item$key;
}): void => {
    trackEvent({
        ...getBaseTrackingObject({ item }),
        noninteraction: 1,
        interaction_type: 'pricing guidance range',
        step_interaction_name: STEP_INTERACTION_DISPLAY,
    });
};

export const trackPricingGuidanceModuleView = ({
    item,
}: {
    item: pricingGuidanceTracking_item$key;
}): void => {
    trackEvent({
        ...getBaseTrackingObject({ item }),
        action: 'pricing guidance module reached',
        noninteraction: 1,
        interaction_type: 'pricing guidance module',
        step_interaction_name: STEP_INTERACTION_DISPLAY,
    });
};

export const trackPricingGuidanceModuleTooltipView = ({
    item,
    trigger,
}: {
    item: pricingGuidanceTracking_item$key;
    trigger?: 'siderail';
}): void => {
    trackEvent({
        ...getBaseTrackingObject({ item }),
        action: 'pricing guidance info icon clicked',
        noninteraction: 0,
        interaction_type: 'tooltip',
        step_interaction_name: STEP_INTERACTION_DISPLAY,
        trigger: `pricing guidance - ${trigger || 'in module'}`,
    });
};

export const trackPricingGuidanceSimilarSoldClick = ({
    item,
}: {
    item: pricingGuidanceTracking_item$key;
}): void => {
    const interaction_type = 'similar sold items clicked';
    trackEvent({
        ...getBaseTrackingObject({ item }),
        action: interaction_type,
        noninteraction: 0,
        interaction_type,
        step_interaction_name: STEP_INTERACTION_LINK_CLICK,
    });
};
